
import React from 'react';
import { 
  LineChart, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  Line,
  ReferenceLine
} from "recharts";
import { formatDateValue } from "../GrowthDataUtils";
import { formatYAxisTick, isMetricUnit } from "./YAxisFormatter";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

interface MainChartContentProps {
  data: any[];
  showAverageLine: boolean;
  averageData: any[];
  displayOriginalValue: number | null;
  valueKey: string;
  unitLabel: string;
  yAxisDomain: [number, number] | ['auto', 'auto'];
  chartConfig: any;
  animationActive: boolean;
  isHeight: boolean;
}

export function MainChartContent({
  data,
  showAverageLine,
  averageData,
  displayOriginalValue,
  valueKey,
  unitLabel,
  yAxisDomain,
  chartConfig,
  animationActive,
  isHeight
}: MainChartContentProps) {
  const { language } = useLanguage();
  const metricName = isHeight ? "Height" : "Weight";
  const mainColor = chartConfig[isHeight ? "height" : "weight"]?.theme?.light || "#4f46e5";
  const originalColor = chartConfig.original?.theme?.light || "#f97316";
  const averageColor = chartConfig.average?.theme?.light || "#10b981";
  
  console.log("[MainChartContent] valueKey:", valueKey);
  console.log("[MainChartContent] displayOriginalValue:", displayOriginalValue, unitLabel);
  console.log("[MainChartContent] data length:", data.length);
  
  // Format the birth measurement value for display with appropriate precision
  const formattedBirthValue = displayOriginalValue !== null 
    ? displayOriginalValue.toFixed(isHeight ? 1 : unitLabel === 'lb' ? 2 : 1)
    : "N/A";
  
  return (
    <LineChart 
      data={data} 
      margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
      width={500}
      height={300}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis 
        dataKey="formattedDate" 
        tick={{ fontSize: 12 }}
        tickFormatter={(value) => formatDateValue(value)}
        height={40}
      />
      <YAxis 
        label={{ 
          value: unitLabel, 
          angle: -90, 
          position: 'insideLeft',
          offset: 0
        }}
        domain={yAxisDomain}
        allowDataOverflow={false}
        width={50}
        tickFormatter={(value) => formatYAxisTick(value, unitLabel).toString()}
      />
      <Tooltip
        content={(props) => {
          if (!props.active || !props.payload || !props.payload.length) return null;
          
          return (
            <div className="rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl min-w-[8rem]">
              {props.label && (
                <div className="font-medium">
                  {props.label === "Birth" 
                    ? t(isHeight ? 'statistics.birthHeight' : 'statistics.birthWeight', language) 
                    : `Date: ${props.label}`}
                </div>
              )}
              <div className="grid gap-1.5 mt-1">
                {props.payload.map((entry: any, index: number) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <div
                      className="h-2 w-2 shrink-0 rounded-[2px]"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-muted-foreground">{entry.name}</span>
                    <span className="font-mono font-medium tabular-nums text-foreground ml-auto">
                      {typeof entry.value === 'number' 
                        ? `${entry.value.toFixed(isHeight ? 1 : unitLabel === 'lb' ? 2 : 1)} ${unitLabel}` 
                        : `${entry.value} ${unitLabel}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        }}
      />
      <Legend 
        verticalAlign="bottom" 
        height={36} 
        wrapperStyle={{ paddingTop: "10px", marginBottom: "10px" }} 
      />
      
      <Line 
        type="monotone" 
        dataKey={valueKey} 
        name={`${metricName} (${unitLabel})`} 
        stroke={mainColor}
        activeDot={{ r: 8 }} 
        dot={true}
        strokeWidth={2}
        isAnimationActive={animationActive}
        animationDuration={1000}
        animationEasing="ease-in-out"
      />
      
      {displayOriginalValue !== null && (
        <ReferenceLine 
          y={displayOriginalValue} 
          stroke={originalColor}
          strokeDasharray="3 3"
          label={{ 
            value: `${t(isHeight ? 'statistics.birthHeight' : 'statistics.birthWeight', language)}: ${formattedBirthValue} ${unitLabel}`, 
            fill: originalColor,
            position: 'insideBottomRight'
          }}
        />
      )}
      
      {showAverageLine && averageData.length > 0 && (
        <Line 
          type="monotone" 
          data={averageData}
          dataKey="averageValue" 
          name={`Average ${metricName} (${unitLabel})`} 
          stroke={averageColor}
          dot={false}
          strokeWidth={2}
          strokeDasharray="5 5"
          isAnimationActive={animationActive}
          animationDuration={1000}
          animationEasing="ease-in-out"
          animationBegin={300}
        />
      )}
    </LineChart>
  );
}
